import { type TelemetryAttributes, type TelemetryExporter, type TelemetryItem } from './types';

export function createNoopExporter(): TelemetryExporter {
  return { export: () => undefined };
}

export function createConsoleExporter(): TelemetryExporter {
  return {
    export: (items) => {
      for (const item of items) {
        if (item.kind === 'span') {
          console.debug(`[telemetry] span ${item.name} ${Math.round(item.end - item.start)}ms ${item.status}`, item.attributes, item.events);
        } else if (item.kind === 'event') {
          console.debug(`[telemetry] event ${item.name}`, item.attributes);
        } else {
          console.debug(`[telemetry] metric ${item.name}=${item.value}`, item.attributes);
        }
      }
    },
  };
}

export interface OtlpExporterOptions {
  /** Collector base URL; `/v1/traces` and `/v1/metrics` are appended. */
  url: string;
  headers?: Record<string, string>;
  fetch?: typeof fetch;
  serviceName?: string;
}

type OtlpValue = { stringValue: string } | { intValue: string } | { doubleValue: number } | { boolValue: boolean };
interface OtlpKeyValue {
  key: string;
  value: OtlpValue;
}

function otlpValue(v: string | number | boolean): OtlpValue {
  if (typeof v === 'boolean') return { boolValue: v };
  if (typeof v === 'number') return Number.isInteger(v) ? { intValue: String(v) } : { doubleValue: v };
  return { stringValue: v };
}

export function toOtlpAttributes(attrs: TelemetryAttributes | undefined): OtlpKeyValue[] {
  if (!attrs) return [];
  const out: OtlpKeyValue[] = [];
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined) continue;
    out.push({ key, value: otlpValue(value) });
  }
  return out;
}

function nanos(ms: number): string {
  return String(Math.round(ms * 1_000_000));
}

const SCOPE = { name: '@zagru/platform', version: '0.1.0' };

/** Builds the OTLP/HTTP JSON `resourceSpans` payload for spans and events (events become zero-length spans). */
export function buildTracesPayload(items: TelemetryItem[], serviceName: string): Record<string, unknown> | null {
  const spans: Record<string, unknown>[] = [];
  for (const item of items) {
    if (item.kind === 'span') {
      spans.push({
        traceId: item.traceId,
        spanId: item.spanId,
        parentSpanId: item.parentSpanId,
        name: item.name,
        kind: 1,
        startTimeUnixNano: nanos(item.start),
        endTimeUnixNano: nanos(item.end),
        attributes: toOtlpAttributes(item.attributes),
        events: item.events.map((e) => ({ name: e.name, timeUnixNano: nanos(e.time), attributes: toOtlpAttributes(e.attributes) })),
        status: { code: item.status === 'error' ? 2 : 1 },
      });
    } else if (item.kind === 'event') {
      spans.push({
        traceId: randomHex(32),
        spanId: randomHex(16),
        name: item.name,
        kind: 1,
        startTimeUnixNano: nanos(item.time),
        endTimeUnixNano: nanos(item.time),
        attributes: [...toOtlpAttributes(item.attributes), { key: 'zagru.telemetry.kind', value: { stringValue: 'event' } }],
        events: [],
        status: { code: 1 },
      });
    }
  }
  if (spans.length === 0) return null;
  return {
    resourceSpans: [
      {
        resource: { attributes: toOtlpAttributes({ 'service.name': serviceName }) },
        scopeSpans: [{ scope: SCOPE, spans }],
      },
    ],
  };
}

/** Builds the OTLP/HTTP JSON `resourceMetrics` payload (each metric as a gauge data point). */
export function buildMetricsPayload(items: TelemetryItem[], serviceName: string): Record<string, unknown> | null {
  const metrics = items.filter((i): i is Extract<TelemetryItem, { kind: 'metric' }> => i.kind === 'metric');
  if (metrics.length === 0) return null;
  const byName = new Map<string, Extract<TelemetryItem, { kind: 'metric' }>[]>();
  for (const m of metrics) {
    const list = byName.get(m.name) ?? [];
    list.push(m);
    byName.set(m.name, list);
  }
  return {
    resourceMetrics: [
      {
        resource: { attributes: toOtlpAttributes({ 'service.name': serviceName }) },
        scopeMetrics: [
          {
            scope: SCOPE,
            metrics: Array.from(byName.entries()).map(([name, points]) => ({
              name,
              gauge: {
                dataPoints: points.map((p) => ({
                  timeUnixNano: nanos(p.time),
                  asDouble: p.value,
                  attributes: toOtlpAttributes(p.attributes),
                })),
              },
            })),
          },
        ],
      },
    ],
  };
}

export function randomHex(length: number): string {
  const bytes = new Uint8Array(length / 2);
  if (globalThis.crypto?.getRandomValues) globalThis.crypto.getRandomValues(bytes);
  else for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * OTLP/HTTP JSON exporter. Uses `navigator.sendBeacon` when the page is hiding (keepalive) and
 * fetch otherwise; failures are swallowed so telemetry never breaks the app.
 */
export function createOtlpExporter(options: OtlpExporterOptions): TelemetryExporter {
  const base = options.url.replace(/\/+$/, '');
  const serviceName = options.serviceName ?? 'zagru-web';
  const fetchImpl: typeof fetch = options.fetch ?? ((...args) => globalThis.fetch(...args));
  const headers = { 'Content-Type': 'application/json', ...(options.headers ?? {}) };
  const hasCustomHeaders = Object.keys(options.headers ?? {}).length > 0;

  async function send(url: string, payload: Record<string, unknown>): Promise<void> {
    const body = JSON.stringify(payload);
    const hidden = typeof document !== 'undefined' && document.visibilityState === 'hidden';
    if (hidden && !hasCustomHeaders && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      try {
        if (navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }))) return;
      } catch {
        // fall back to fetch
      }
    }
    try {
      await fetchImpl(url, { method: 'POST', headers, body, keepalive: true });
    } catch {
      // drop silently
    }
  }

  return {
    export: async (items) => {
      const traces = buildTracesPayload(items, serviceName);
      const metrics = buildMetricsPayload(items, serviceName);
      await Promise.all([traces ? send(`${base}/v1/traces`, traces) : undefined, metrics ? send(`${base}/v1/metrics`, metrics) : undefined]);
    },
  };
}
